/**
 * Форма ручного ввода паспорта — `/api/supervisor/passport-form`.
 *
 * Состав формы приходит с сервера: он собирается из реестра показателей, того
 * же, что питает Excel-шаблон. Клиент не хранит собственного каталога — иначе
 * подписи, единицы и адреса записи неизбежно разъезжаются с серверными.
 *
 * Здесь только типы контракта и тонкие обёртки над axios; вся логика раскладки
 * и валидации — на странице и на сервере соответственно.
 */
import { api } from './api';

// ------------------------------------------------------------------ контракт

/** Тип значения показателя — определяет контрол ввода и приведение при отправке. */
export type Dtype = 'int' | 'float' | 'percent' | 'str' | 'date' | 'bool';

export interface PassportField {
  key: string;
  label_ru: string;
  label_kz: string;
  unit: string | null;
  dtype: Dtype;
  value: unknown;
  /** Заполнен — показатель обычно приходит из «Мониторинга СНП». */
  snp_source: string | null;
  /**
   * Оператор перевёл показатель на ручной ввод — по ЭТОЙ территории и году.
   * Хранится на сервере (`source_mode`), меняется `setSourceMode`.
   */
  manual_mode: boolean;
}

export interface PassportColumn {
  column: string;
  label_ru: string;
  label_kz: string;
  unit: string | null;
  dtype: Dtype;
  /** Колонка задаёт личность строки (продукт, отрасль) — показываем текстом. */
  readonly: boolean;
}

export interface PassportBlock {
  id: string;
  table: string;
  label_ru: string;
  label_kz: string;
  /** Заполнены у подблоков, различающихся типом строки (СЭР / Продовольствие). */
  discriminator_column: string | null;
  discriminator_value: string | null;
  columns: PassportColumn[];
  rows: Record<string, unknown>[];
  snp_source: boolean;
  /** Выбор источника по блоку — как у показателя (см. `PassportField`). */
  manual_mode: boolean;
  /**
   * Набор строк канонический (справочник продуктов): строки не добавляются и не
   * удаляются, колонки с `readonly` заполнены сервером.
   */
  fixed_rows: boolean;
}

export interface PassportSection {
  title_ru: string;
  title_kz: string;
  fields: PassportField[];
  blocks: PassportBlock[];
}

export interface PassportForm {
  kato: number;
  name_ru: string;
  name_kz: string;
  level: number;
  year: number;
  sections: PassportSection[];
}

export interface SaveFormRequest {
  kato: number;
  year: number;
  /** Только изменённые показатели. */
  scalars: Record<string, unknown>;
  /** Только изменённые блоки — каждый целиком. */
  blocks: {
    table: string;
    discriminator_value: string | null;
    rows: Record<string, unknown>[];
  }[];
}

export interface SaveFormResult {
  saved_fields: number;
  saved_blocks: number;
  ignored: string[];
}

export const getPassportForm = async (
  kato: number,
  year: number,
): Promise<PassportForm> => {
  const response = await api.get<PassportForm>('/supervisor/passport-form', {
    params: { kato, year },
  });
  return response.data;
};

export const savePassportForm = async (
  body: SaveFormRequest,
): Promise<SaveFormResult> => {
  const response = await api.post<SaveFormResult>('/supervisor/passport-form', body);
  return response.data;
};

/** Режим ввода показателя: значение источника или своё, введённое оператором. */
export type SourceMode = 'source' | 'manual';

/**
 * Запомнить выбор источника по показателю или блоку.
 *
 * Отдельный запрос, а не часть «Сохранить»: выбор определяет, что оператор может
 * править ПРЯМО СЕЙЧАС, и ждать кнопки не может. Ключ — тот же, что форма
 * прислала в GET (`field.key` либо `block.id`), гранулярность на сервере —
 * показатель × КАТО × год: раньше выбор жил в `localStorage` и был общим на все
 * территории.
 */
export const setSourceMode = async (
  kato: number,
  year: number,
  key: string,
  mode: SourceMode,
): Promise<void> => {
  await api.put('/supervisor/passport-form/source-mode', { kato, year, key, mode });
};

const asObj = (v: unknown): Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : {};

/**
 * Ошибка, на которую сервер прислал готовый текст на ru и kz.
 *
 * Бэкенд отвечает так на всё, где виноват вход оператора: не тот формат файла
 * шаблона, не изображение, слишком большой файл, негодная рамка кадрирования.
 * Такие сообщения нужно показывать как есть — они точнее любого нашего
 * «не удалось загрузить».
 */
export class ApiMessageError extends Error {
  constructor(
    readonly messageRu: string,
    readonly messageKz: string,
  ) {
    super(messageRu);
    this.name = 'ApiMessageError';
  }
}

/** Разобрать `detail: {message_ru, message_kz}` из ответа. `null` — не наш случай. */
const asMessageError = (error: unknown): ApiMessageError | null => {
  const detail = asObj(asObj(asObj(asObj(error).response).data).detail);
  if (typeof detail.message_ru !== 'string') return null;
  return new ApiMessageError(detail.message_ru, String(detail.message_kz ?? ''));
};

// ------------------------------------------------------- фото акима (FR-4.7)

/** Рамка кадрирования в пикселях ИСХОДНОГО изображения (после EXIF-поворота). */
export interface PhotoCrop {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface AkimPhotoResult {
  /** Новое значение `general.akim_image_id`. */
  image_id: string;
  /** Прямая ссылка на блоб; `null` у mock-хранилища — тогда см. `akimPhotoUrl`. */
  image_url: string | null;
  file_id: number;
  /** Пропорция, к которой сервер приводит фото: по ней рисуется рамка. */
  aspect: [number, number];
  message_ru: string;
  message_kz: string;
}

/**
 * Ссылка на фото по id блоба. Отдаёт публичный эндпоинт без токена, поэтому
 * годится прямо в `<img src>`: у тега нет наших axios-интерцепторов, и
 * подписанный запрос он бы не сделал.
 */
export const akimPhotoUrl = (imageId: string): string =>
  `/api/public/image/load?imageId=${encodeURIComponent(imageId)}`;

/**
 * Загрузить фото акима территории, опционально с рамкой кадрирования.
 *
 * Файл уходит как есть, а рамка — числами: так сервер режет ОРИГИНАЛ и сам
 * приводит результат к единому размеру. Если бы кадрировал браузер, картинка
 * пережималась бы дважды и качество зависело бы от устройства оператора.
 */
export const uploadAkimPhoto = async (
  kato: number,
  year: number,
  file: File,
  crop?: PhotoCrop,
): Promise<AkimPhotoResult> => {
  const body = new FormData();
  body.append('file', file);
  const params: Record<string, number> = { kato, year };
  if (crop !== undefined) {
    params.crop_x = Math.round(crop.x);
    params.crop_y = Math.round(crop.y);
    params.crop_w = Math.round(crop.width);
    params.crop_h = Math.round(crop.height);
  }
  try {
    const response = await api.post<AkimPhotoResult>(
      '/supervisor/passport-form/akim-photo',
      body,
      { params },
    );
    return response.data;
  } catch (error) {
    // 400/413 с ru/kz-сообщением (не изображение, велик файл, негодная рамка) —
    // показываем его текстом, а не «что-то пошло не так».
    const known = asMessageError(error);
    if (known !== null) throw known;
    throw error;
  }
};

// ---------------------------------------------- Excel-шаблон ввода данных

/**
 * Массовый ввод через Excel — вторая дорожка к тем же данным и тому же реестру.
 * Форма удобна для точечной правки, шаблон — когда заполняют территорию сразу
 * за несколько лет или собирают данные офлайн: в шаблоне семь колонок годов.
 */

export interface TemplatePreview {
  /** Отчётный год шаблона — центр окна колонок годов. */
  year: number | null;
  /** Годы, за которые в файле есть хоть одно значение. */
  years: number[];
  /** Все годы колонок шаблона (E:K), заполненные и пустые. */
  template_years: number[];
  region_mismatch: boolean;
  recognized_katos_total: number;
  matched_columns: string[];
  unknown_columns: string[];
  foreign_katos: number[];
  rejected_cells: { sheet: string; row: number; kato: number; key: string; reason: string }[];
  /** Блок заменяется целиком — и отдельно за каждый год, а не «за территорию». */
  overwrite_warnings: { block: string; kato: number; year: number; incoming_rows: number }[];
  message_ru: string;
  message_kz: string;
}

export interface TemplateApplyResult {
  applied: { scalars: number; blocks: number; years: number[] };
  skipped: { empty_blocks: number; errors: number };
  foreign_excluded: number;
  message_ru: string;
  message_kz: string;
}

/** Ошибка формата шаблона — бэкенд отвечает 400 с ru/kz-сообщением. */
export class TemplateFormatError extends ApiMessageError {
  constructor(messageRu: string, messageKz: string) {
    super(messageRu, messageKz);
    this.name = 'TemplateFormatError';
  }
}

const asFormatError = (error: unknown): TemplateFormatError | null => {
  const body = asObj(asObj(asObj(error).response as unknown).data as unknown);
  if (body.error !== 'bad_format' && body.error !== 'forbidden') return null;
  return new TemplateFormatError(
    String(body.message_ru ?? ''),
    String(body.message_kz ?? ''),
  );
};

/**
 * Скачать шаблон ВЫБРАННОЙ территории, уже заполненный её текущими данными.
 *
 * Ровно один КАТО — тот, что открыт в кабинете. Уровень территории роли не
 * играет: область, район, округ и село выгружаются одинаково. Многолистовые
 * книги (район со всеми округами и селами) остались за офлайн-скриптом на
 * бэкенде — оператору кабинета они только мешали бы искать свой лист.
 */
export const downloadTemplate = async (
  kato: number,
  year: number,
): Promise<{ blob: Blob; filename: string }> => {
  const response = await api.get<Blob>('/supervisor/passport-template/download', {
    params: { kato, year },
    responseType: 'blob',
  });
  // Имя файла бэкенд отдаёт в RFC-5987 (`filename*=UTF-8''…`) — иначе кириллица
  // в браузере превратится в кракозябры.
  const disposition = String(asObj(response.headers)['content-disposition'] ?? '');
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  const filename = encoded ? decodeURIComponent(encoded[1]) : 'passport_template.xlsx';
  return { blob: response.data, filename };
};

const postTemplate = async <T>(path: string, file: File): Promise<T> => {
  const body = new FormData();
  body.append('file', file);
  try {
    const response = await api.post<T>(path, body);
    return response.data;
  } catch (error) {
    const formatError = asFormatError(error);
    if (formatError !== null) throw formatError;
    throw error;
  }
};

export const previewTemplate = (file: File): Promise<TemplatePreview> =>
  postTemplate<TemplatePreview>('/supervisor/passport-template/preview', file);

export const applyTemplate = (file: File): Promise<TemplateApplyResult> =>
  postTemplate<TemplateApplyResult>('/supervisor/passport-template/apply', file);
